import { AddressAutocomplete, applySelectedAddress } from '../components/AddressAutocomplete';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout, Card, FormInput, Button, TermsAcceptanceHistory } from '../components';
import { ContractStatusStyleSetting } from '../components/ContractStatusStyleSetting';
import { Breadcrumb } from '../components/Breadcrumb';
import { BankPicker, useBankOptions } from '../components/BankPicker';
import { userApi } from '../services/api';
import { saveErrorText } from '../utils/apiError';
import type { BankSummary } from '../types';

interface UserProfile {
  id: string;
  email: string;
  fullName: string;
  phone: string;
  address: string;
  postalCode: string;
  city: string;
  personaType: string;
  banks?: BankSummary[];
}

export function CompanyProfile() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    address: '',
    postalCode: '',
    city: ''
  });
  // A Privatperson keeps at least one bank on the profile (ClickUp 869eu6fpm).
  const [bankIds, setBankIds] = useState<string[]>([]);
  const [banksTouched, setBanksTouched] = useState(false);
  const isPerson = profile?.personaType === 'person';
  const bankOptions = useBankOptions(isPerson);

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProfile = async () => {
    try {
      const data = await userApi.getProfile();
      setProfile(data);
      setFormData({
        fullName: data.fullName || '',
        phone: data.phone || '',
        address: data.address || '',
        postalCode: data.postalCode || '',
        city: data.city || ''
      });
      setBankIds(data.banks?.map(bank => bank.id) ?? []);
    } catch (error) {
      console.error('Failed to load profile:', error);
      setMessage({ type: 'error', text: t('profile.loadError') });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };


  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isPerson && !bankIds.length) {
      setBanksTouched(true);
      return;
    }
    setIsSaving(true);
    setMessage(null);

    try {
      await userApi.updateProfile({
        phone: formData.phone,
        address: formData.address,
        postalCode: formData.postalCode,
        city: formData.city,
        ...(isPerson ? { bankIds } : {})
      });
      setMessage({ type: 'success', text: t('profile.saveSuccess') });
      // The reload runs in the background (ClickUp 869fgxq45).
      void loadProfile();
    } catch (error) {
      console.error('Failed to save profile:', error);
      setMessage({ type: 'error', text: saveErrorText(t, error, 'profile.saveError') });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelProfile = () => {
    if (profile) {
      setFormData({
        fullName: profile.fullName || '',
        phone: profile.phone || '',
        address: profile.address || '',
        postalCode: profile.postalCode || '',
        city: profile.city || ''
      });
      setBankIds(profile.banks?.map(bank => bank.id) ?? []);
      setBanksTouched(false);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('profile.title')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/home' },
            { label: t('profile.title') }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t('profile.description')}</p>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show`}>
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
        </div>
      )}

      {/* Profile Section */}
      <Card title={t('profile.profileSection')} className="mb-4">
        <form onSubmit={handleSaveProfile}>
          <div className="row">
            <div className="col-md-6 mb-3">
              <FormInput
                name="fullName"
                disabled
                label={t('profile.fields.fullName')}
                value={formData.fullName}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">{t('profile.fields.email')}</label>
              <input
                type="email"
                className="form-control bg-light"
                value={profile?.email || ''}
                disabled
              />
              <small className="text-muted">{t('profile.fields.emailHelp')}</small>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <FormInput
                type="tel"
                name="phone"
                label={t('profile.fields.phone')}
                value={formData.phone}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-md-6 mb-3">
              <AddressAutocomplete
                name="address"
                onAddressSelect={address => setFormData(previous => applySelectedAddress(previous, address))}
                label={t('profile.fields.address')}
                value={formData.address}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <FormInput
                name="postalCode"
                label={t('profile.fields.postalCode')}
                value={formData.postalCode}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-md-6 mb-3">
              <FormInput
                name="city"
                label={t('profile.fields.city')}
                value={formData.city}
                onChange={handleInputChange}
              />
            </div>
          </div>

          {isPerson && bankOptions.loadError && (
            <div className="alert alert-danger" role="alert">{t('banks.loadError')}</div>
          )}
          {isPerson && bankOptions.banks && (
            <BankPicker
              banks={bankOptions.banks}
              value={bankIds}
              current={profile?.banks}
              disabled={isSaving}
              showRequired={banksTouched}
              onChange={next => {
                setBanksTouched(true);
                setBankIds(next);
              }}
            />
          )}

          <div className="d-flex gap-2 justify-content-end">
            <Button type="button" variant="secondary" onClick={handleCancelProfile}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" isLoading={isSaving}>
              {t('common.save')}
            </Button>
          </div>
        </form>
      </Card>

      <ContractStatusStyleSetting />


      <TermsAcceptanceHistory />
    </DashboardLayout>
  );
}
